import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import ProfileSection from "./components/ProfileSection";
import Navigation from "./components/Navigation";
import Projects from "./components/Projects";
import Expertise from "./components/Services";
import Tools from "./components/Tools";
import AboutMe from "./components/AboutMe";
import ContactUs from "./components/ContactUs";

export default function App() {
  const [activeTab, setActiveTab] = useState("About Me");
  const [isScrolled, setIsScrolled] = useState(false);

  // Scroll to top when tab changes
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [activeTab]);

  useEffect(() => {
    let ticking = false;
    let lastScrollY = 0;
    
    const handleScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          const scrollTop = window.scrollY;
          
          // Add hysteresis to prevent dancing - different thresholds for scrolling up vs down
          const scrollingDown = scrollTop > lastScrollY;
          const threshold = isScrolled ? 60 : 100; // Lower threshold when already scrolled
          
          if (scrollingDown && scrollTop > threshold) {
            setIsScrolled(true);
          } else if (!scrollingDown && scrollTop < threshold - 20) {
            setIsScrolled(false);
          }
          
          lastScrollY = scrollTop;
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isScrolled]);

  const renderActiveSection = () => {
    switch (activeTab) {
      case "About Me":
        return <AboutMe />;
      case "Projects":
        return <Projects />;
      case "Expertise":
        return <Expertise setActiveTab={setActiveTab} />;
      case "Tools":
        return <Tools setActiveTab={setActiveTab} />;
      case "Contact Us":
        return <ContactUs />;
      default:
        return <Projects />;
    }
  };

  const pageVariants = {
    initial: { opacity: 0, y: 20 },
    animate: { 
      opacity: 1, 
      y: 0,
      transition: {
        duration: 0.8,
        ease: "easeOut",
        staggerChildren: 0.2
      }
    }
  };

  const sectionVariants = {
    initial: { opacity: 0, y: 20 },
    animate: { 
      opacity: 1, 
      y: 0,
      transition: {
        duration: 0.3,
        ease: "easeOut"
      }
    },
    exit: { 
      opacity: 0, 
      y: -20,
      transition: {
        duration: 0.2,
        ease: "easeIn"
      }
    }
  };

  return (
    <motion.div 
      className="min-h-screen bg-white"
      initial="initial"
      animate="animate"
      variants={pageVariants}
    >
      {/* Sticky Header Section */}
      <motion.div 
        className="sticky top-0 z-50 bg-white border-b border-gray-100"
        animate={{
          paddingTop: isScrolled ? "8px" : "16px",
          paddingBottom: isScrolled ? "8px" : "28px"
        }}
        transition={{ duration: 0.5, ease: [0.25, 0.46, 0.45, 0.94] }}
      >
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div variants={sectionVariants}>
            <ProfileSection isScrolled={isScrolled} />
          </motion.div>
          
          <motion.div 
            variants={sectionVariants}
            animate={{
              marginTop: isScrolled ? "8px" : "16px"
            }}
            transition={{ duration: 0.5, ease: [0.25, 0.46, 0.45, 0.94] }}
          >
            <Navigation activeTab={activeTab} setActiveTab={setActiveTab} isScrolled={isScrolled} />
          </motion.div>
        </div>
      </motion.div>

      {/* Scrollable Content Section */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8 lg:py-10">
        <AnimatePresence mode="wait">
          <motion.div
            key={activeTab}
            variants={sectionVariants}
            initial="initial"
            animate="animate"
            exit="exit"
            className="min-h-[70vh]"
          >
            {renderActiveSection()}
          </motion.div>
        </AnimatePresence>
      </div>
    </motion.div>
  );
} 